// apps/roleforge-app/src/lib/use-respond-session.ts
//
// Phase 10 Task 10.1 — exchange a stakeholder invitation token for a Supabase
// session cookie. The token comes from the email link; the server's
// POST /api/roleforge/respond/:token/session endpoint sets the
// `sb-stakeholder-session` httpOnly cookie and returns the audit/thread
// identifiers in the body. After this hook resolves to `ready`, the rest of
// the stakeholder shell can authenticate by including credentials in subsequent
// requests.

import { useEffect, useState } from 'react';

export interface RespondSession {
  invitation_id: string;
  audit_id: string;
  thread_id: string;
}

export interface RespondSessionState {
  state: 'loading' | 'ready' | 'not_found' | 'revoked' | 'error';
  session: RespondSession | null;
  error: string | null;
}

const API_BASE =
  import.meta.env.VITE_VIBECONTROL_API_URL ?? 'https://api.vibecontrol.app';

export function useRespondSession(token: string | undefined): RespondSessionState {
  const [state, setState] = useState<RespondSessionState>({
    state: 'loading',
    session: null,
    error: null,
  });

  useEffect(() => {
    if (!token) {
      setState({ state: 'not_found', session: null, error: null });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/roleforge/respond/${token}/session`, {
          method: 'POST',
          credentials: 'include',
        });
        if (cancelled) return;
        if (res.status === 404) {
          setState({ state: 'not_found', session: null, error: null });
          return;
        }
        if (res.status === 410) {
          setState({ state: 'revoked', session: null, error: null });
          return;
        }
        if (!res.ok) {
          setState({ state: 'error', session: null, error: `status ${res.status}` });
          return;
        }
        const session = (await res.json()) as RespondSession;
        setState({ state: 'ready', session, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            state: 'error',
            session: null,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}
